import { memo, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { HelpMenuButton } from '@/components/common/HelpMenuButton';
import { Book1, Flag, Keyboard, PlayCircle } from '@/components/icons';
import { useTestBlockSelection } from '@/components/preview/block-selection/TestBlockSelectionContext';
import { HelpPage } from '@/pages/constants';
import { createPageUrl } from '@/utils';
import { usePageScrollTracker } from '../hooks/usePageScrollTracker';
import { FloatingControls } from './FloatingControls';
import { PageScrollIndicator } from './PageScrollIndicator';

const CONTROLS_HIDE_DELAY_MS = 2000;

type PreviewActivityLayerProps = {
    containerRef: RefObject<HTMLDivElement | null>;
    totalPages: number;
    rightSidebarWidth: number;
    isSidebarExpanded: boolean;
    hideFloatingControls?: boolean;
    onOpenShortcuts: () => void;
    onReplayTour: () => void;
};

type PreviewActivityLayerInnerProps = PreviewActivityLayerProps & {
    hideForBlockSelection: boolean;
};

const helpActions = [
    {
        id: 'editor-tour',
        label: 'Tour do editor',
        icon: Flag,
    },
    {
        id: 'keyboard-shortcuts',
        label: 'Atalhos de teclado',
        icon: Keyboard,
    },
    {
        id: 'video-lessons',
        label: 'Vídeo-aulas',
        icon: PlayCircle,
        href: 'https://www.youtube.com/@LaudoPsi',
        newTab: true,
    },
    {
        id: 'help-center',
        label: 'Central de Ajuda',
        icon: Book1,
        href: createPageUrl(HelpPage),
        newTab: true,
        separatorBefore: true,
    },
] as const;

function PreviewActivityLayerComponent({
    containerRef,
    totalPages,
    rightSidebarWidth,
    isSidebarExpanded,
    hideFloatingControls = false,
    hideForBlockSelection,
    onOpenShortcuts,
    onReplayTour,
}: PreviewActivityLayerInnerProps) {
    const { scrolledPage, isIndicatorVisible } = usePageScrollTracker(containerRef, totalPages);
    const [showControls, setShowControls] = useState(false);
    const controlsVisibleRef = useRef(false);
    const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activityFrameRef = useRef<number | null>(null);

    const revealControls = useCallback(() => {
        if (!controlsVisibleRef.current) {
            controlsVisibleRef.current = true;
            setShowControls(true);
        }

        if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
        }

        controlsTimeoutRef.current = setTimeout(() => {
            controlsVisibleRef.current = false;
            setShowControls(false);
        }, CONTROLS_HIDE_DELAY_MS);
    }, []);

    const scheduleActivity = useCallback(() => {
        if (activityFrameRef.current !== null) return;

        activityFrameRef.current = window.requestAnimationFrame(() => {
            activityFrameRef.current = null;
            revealControls();
        });
    }, [revealControls]);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        container.addEventListener('scroll', scheduleActivity, { passive: true });
        container.addEventListener('mousemove', scheduleActivity, { passive: true });

        return () => {
            container.removeEventListener('scroll', scheduleActivity);
            container.removeEventListener('mousemove', scheduleActivity);
        };
    }, [containerRef, scheduleActivity]);

    useEffect(() => {
        return () => {
            if (controlsTimeoutRef.current) {
                clearTimeout(controlsTimeoutRef.current);
            }

            if (activityFrameRef.current !== null) {
                window.cancelAnimationFrame(activityFrameRef.current);
            }
        };
    }, []);

    const resolvedHelpActions = useMemo(
        () =>
            helpActions.map((action) => {
                if (action.id === 'keyboard-shortcuts') {
                    return { ...action, onSelect: onOpenShortcuts };
                }

                if (action.id === 'editor-tour') {
                    return { ...action, onSelect: onReplayTour };
                }

                return action;
            }),
        [onOpenShortcuts, onReplayTour],
    );

    return (
        <>
            <PageScrollIndicator
                currentPage={scrolledPage}
                totalPages={totalPages}
                isVisible={isIndicatorVisible}
                rightSidebarWidth={rightSidebarWidth}
            />

            {!hideFloatingControls && (
                <FloatingControls
                    showControls={showControls && !hideForBlockSelection}
                    isSidebarExpanded={isSidebarExpanded}
                    rightSidebarWidth={rightSidebarWidth}
                />
            )}

            <HelpMenuButton
                variant="floating"
                isVisible={!hideForBlockSelection}
                rightOffset={rightSidebarWidth}
                actions={resolvedHelpActions}
            />
        </>
    );
}

const PreviewActivityLayerInner = memo(PreviewActivityLayerComponent);

/**
 * While the user is selecting or carrying test blocks, the selection toolbar
 * owns the bottom edge — the zoom pill and help button fade out so they don't
 * compete with it.
 */
export function PreviewActivityLayer(props: PreviewActivityLayerProps) {
    const { enabled, selectedIds, clipboardState } = useTestBlockSelection();
    const hideForBlockSelection = enabled && (selectedIds.size > 0 || clipboardState !== null);

    return <PreviewActivityLayerInner {...props} hideForBlockSelection={hideForBlockSelection} />;
}
